import i18n from '@/i18n';
import moment from 'moment';

import { isValidDate } from '@/utils/handlers';
import { omit } from 'lodash';

const STR_DATE_TIME = 'YYYY-MM-DD HH:mm';
const STR_DATE = 'YYYY-MM-DD';
const STR_DATE_CUSTOM = 'YYYY-M-D';
const STR_FULL_DATE_TIME_JP = 'YYYY年MM月DD日 HH:mm';
const IMAGE_EXTENSIONS = ['png', 'jpeg', 'jpg', 'gif', 'bmp', 'tiff', 'svg', 'eps', 'raw', 'nef', 'cr2', 'arw', 'webp', 'heif', 'heic', 'ico'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'wmv'];

function removeWhitespace(valueForm) {
    return valueForm.replace(/\s+/g, ' ').trim();
}

function truncateText(text, lengthCut = 100) {
    if (text?.length > lengthCut) {
        return text.slice(0, lengthCut) + '...';
    }
    return text;
}

function renderLineBreaks(text) {
    if (!text) return '';
    return text?.replace(/\n/g, '<br>');
}

function isNumber(evt) {
    evt = evt ? evt : window.event;
    // Allow special keys (Ctrl, Alt, Meta) for shortcuts like Ctrl+C, Ctrl+V, etc.
    if (evt.ctrlKey || evt.altKey || evt.metaKey) {
        return true;
    }
    let charCode = evt.which ? evt.which : evt.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
    } else {
        return true;
    }
}

function formatDateOrTimeJP(dateString, type) {
    const daysInJapanese = ['日', '月', '火', '水', '木', '金', '土'];
    if (type === 'dateTimeJP' || type === 'fullDateTimeJP') {
        const dayOfWeek = moment(dateString).day();
        const dayNameInJapanese = daysInJapanese[dayOfWeek] || '';
        const getTime = dateString ? moment(dateString).format('HH:mm') : '';
        const [year, month, day] = (dateString ? moment(dateString).format(STR_DATE_CUSTOM) : '').split('-');
        return `${year}${i18n.t('text.year')}${month}${i18n.t('text.month')}${day}${i18n.t('text.day')}（${dayNameInJapanese}）${getTime}`;
    } else if (type === 'dateJP') {
        const [year, month, day] = (dateString ? moment(dateString).format(STR_DATE_CUSTOM) : '').split('-');
        return `${year}${i18n.t('text.year')}${month}${i18n.t('text.month')}${day}${i18n.t('text.day')}`;
    }
}

function formatDateOrTime(paramDate, type) {
    if (type === 'dateTime') {
        return paramDate ? moment(paramDate).format('HH:mm') : '';
    } else if (type === 'date') {
        return paramDate ? moment(paramDate).format(STR_DATE) : '';
    }
}

function formatEveryDayJapanese(date, type) {
    switch (type) {
        case 'everyDay':
            return moment(date).locale('ja').format('YYYY年MM月DD日（ddd）');
        case 'everyDayHHmm':
            return moment(date).locale('ja').format('YYYY年MM月DD日（ddd）HH:mm');
        case 'YYYMMDDHHmm':
            return moment(date).locale('ja').format(STR_FULL_DATE_TIME_JP);
    }
}

function formatMergeDateAndTime(date, time) {
    if (!date && !time) return '';
    return moment(date + '' + time, STR_DATE_TIME).format(STR_DATE_TIME);
}

function formatDateCommon(dateString, type = null) {
    // type: ['dateJP', 'dateTimeJP', 'date', 'dateTime]
    if (!dateString) return '';
    switch (type) {
        case 'dateJP':
            return formatDateOrTimeJP(dateString, type);
        case 'dateTimeJP':
            return formatDateOrTimeJP(dateString, type);
        case 'fullDateTimeJP':
            return formatDateOrTimeJP(dateString, type);
        case 'date':
            return formatDateOrTime(dateString, type);
        case 'dateTime':
            return formatDateOrTime(dateString, type);
        case 'everyDay':
            return formatEveryDayJapanese(dateString, type);
        case 'everyDayHHmm':
            return formatEveryDayJapanese(dateString, type);
        case 'YYYMMDDHHmm':
            return formatEveryDayJapanese(dateString, type);
        default:
            return '';
    }
}

function getDateFromFullDate(fullDate) {
    let day = fullDate?.split('-')[2];
    let month = fullDate?.split('-')[1];
    let year = fullDate?.split('-')[0];
    return isValidDate(day + '/' + month + '/' + year);
}

function tranferShortCode(dataShortCode, replacements) {
    let result = dataShortCode;
    if (!result) return '';
    result = result.replace(/《(.*?)》/g, (match, p1) => {
        return replacements[p1] !== undefined ? replacements[p1] : '●●円分';
    });
    Object.keys(replacements).forEach((key) => {
        if (key.includes('/')) {
            const pattern = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
            result = result.replace(pattern, replacements[key]);
        }
    });
    return result;
}

function convertTagsToHashTags(tags) {
    if (tags.length <= 0) return '';
    return tags.map((tag) => `#${tag}`);
}

async function handleDownFile(path, filename, onProgress) {
    let nameFile = filename;
    try {
        const response = await fetch(path);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const contentLength = +response.headers.get('Content-Length');
        const reader = response.body.getReader();
        let receivedLength = 0;
        let chunks = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            receivedLength += value.length;
            if (typeof onProgress === 'function') {
                // Pass (received, total, calculatedProgress) for maximum compatibility
                onProgress(receivedLength, contentLength, calculateDownloadProgress(receivedLength, contentLength));
            }
        }
        const blob = new Blob(chunks);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = nameFile;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (err) {
        console.error('Download failed', err);
        throw err;
    }
}

function cleanConfigFormData(formData) {
    if (!formData) return formData;
    Object.keys(formData).forEach((key) => {
        if (key.startsWith('configField')) {
            formData = omit(formData, [`${key}`]);
        }
        if (typeof formData[key] === 'object' && formData[key] !== null) {
            formData[key] = cleanConfigFormData(formData[key]);
        }
    });
    return formData;
}

function isValidDateTime(dateTimeString) {
    return moment(dateTimeString, STR_DATE_TIME, true).isValid();
}

function isDateRangeYear(fullDate, minYear, maxYear) {
    const minYearStr = minYear.toString();
    let yearPattern = '';
    if (minYear === maxYear) {
        yearPattern = `${minYearStr}`;
    } else {
        let yearsRange = [];
        for (let i = minYear; i <= maxYear; i++) {
            yearsRange.push(i.toString());
        }
        yearPattern = `(${yearsRange.join('|')})`;
    }
    const monthPattern = '(0?[1-9]|1[0-2])';
    const dayPattern = '(0?[1-9]|[12][0-9]|3[01])';
    const dateRegex = new RegExp(`^${yearPattern}-${monthPattern}-${dayPattern}$`);
    const isValid = dateRegex.test(fullDate);
    if (!isValid) {
        return false;
    }
    const [year, month, day] = fullDate.split('-').map(Number);
    if (year === maxYear) {
        const daysInMonth = new Date(year, month, 0).getDate();
        if (month === 12 && day > daysInMonth) {
            return false;
        }
    }
    return true;
}

function formattedPostalCode(data) {
    if (!data) return '';
    return data.replace(/(\d{3})(\d{4})/, '$1-$2');
}

function convertKBtoMB(kb) {
    return kb / 1024;
}

function convertMbToKb(mb) {
    return mb * 1024;
}

function formattedAmount(valueAmount) {
    if (valueAmount == 0 || (valueAmount !== null && valueAmount !== undefined && Number(valueAmount))) {
        return Number(valueAmount).toLocaleString();
    }
    return '';
}

function convertLinkTextarea(text) {
    if (!text || !text.toString() || Array.isArray(text)) return '';
    const urlRegex = /(^|\s)(https?:\/\/[^\s]+)($|\s)/g;
    return text.replace(urlRegex, '$1<a href="$2" target="_blank">$2</a>$3').replace(/\n/g, '<br>');
}

function convertedLinkInput(inputLink) {
    if (inputLink?.startsWith('http')) {
        return `<a href="${inputLink}" target="_blank">${inputLink}</a>`;
    }
    return inputLink;
}

function convertHtmlLinks(text) {
    if (!text || !text.toString() || Array.isArray(text)) return '';
    return text.toString().replace(/(<a\s[^>]*>.*?<\/a>)|(<[^>]+>)|(https?:\/\/[^\s<]+)/gi, (match, aTag, otherTag, url) => {
        if (aTag) return aTag;
        if (otherTag) return otherTag;
        if (url) return `<a href="${url}" target="_blank" style="word-break: break-all; color: #007bff;">${url}</a>`;
        return match;
    });
}

function transferHTMLToShortcode(placeholders, html) {
    const div = document.createElement('div');
    div.innerHTML = html;

    Object.entries(placeholders).forEach(([id, replacement]) => {
        const element = div.querySelector(`#${id}`);
        if (element) {
            let replaced = false;
            // Thử thay thế text trong các node con (nếu có)
            for (let node of element.childNodes) {
                if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                    node.textContent = replacement;
                    replaced = true;
                    break; // chỉ thay text đầu tiên
                }
            }
            // Nếu không có textNode hoặc không thay được gì, set trực tiếp innerText
            if (!replaced) {
                element.textContent = replacement;
            }
        }
    });

    return div.innerHTML;
}
function getCurrentDateTimeFormatted() {
    return moment().format('YYYY/MM/DD HH:mm:ss');
}

function formatNumber(n) {
    return n !== null ? n.toString().replace(/[,]+/g, '') : n;
}

function convertToHalfsize(chars) {
    let charsTrim = chars ? chars.trim() : '';
    let ascii = '';
    for (let i = 0, l = charsTrim.length; i < l; i++) {
        let c = chars[i].charCodeAt(0);
        // fix dot fullsize
        if (c === 12290) {
            c = 46;
        }
        if (c >= 0xff00 && c <= 0xffef) {
            c = 0xff & (c + 0x20);
        }
        ascii += String.fromCharCode(c);
    }
    return formatNumber(ascii);
}

function isFullWidth(str) {
    return /[！-～]/.test(str);
}

function generateUniqueBlockName(list, baseName) {
    // Regex: baseName + số (có thể không có số)
    const safeName = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`^${safeName}(\\d+)?$`);
    // Lấy tất cả số đã dùng
    const usedNumbers = list
        .map((item) => {
            const m = item.block_name.match(regex);
            return m ? (m[1] ? parseInt(m[1], 10) : 0) : null;
        })
        .filter((n) => n !== null);
    // Tìm số nhỏ nhất chưa dùng
    let i = 0;
    while (usedNumbers.includes(i)) {
        i++;
    }
    // Nếu i = 0 thì giữ nguyên tên gốc
    return i === 0 ? baseName : `${baseName}${i}`;
}

function getTimeFormat(time, fmTime = 'HH:mm') {
    return moment(time, 'HH:mm:ss').format(fmTime);
}

function formatInputDayOrMonth(valueInput) {
    if (valueInput === null || valueInput === undefined || valueInput === '') {
        return valueInput;
    }
    const strValue = String(valueInput);
    if (valueInput >= 1 && valueInput <= 9 && strValue.length === 1) {
        return '0' + strValue;
    }
    return strValue;
}

function calculateDownloadProgress(received, total) {
    if (total > 0) {
        return Math.round((received / total) * 100);
    }
    return 0;
}

export {
    STR_DATE_TIME,
    STR_DATE,
    STR_FULL_DATE_TIME_JP,
    IMAGE_EXTENSIONS,
    VIDEO_EXTENSIONS,
    removeWhitespace,
    truncateText,
    renderLineBreaks,
    isNumber,
    getDateFromFullDate,
    tranferShortCode,
    convertTagsToHashTags,
    handleDownFile,
    cleanConfigFormData,
    isValidDateTime,
    formatDateCommon,
    formatMergeDateAndTime,
    isDateRangeYear,
    formattedPostalCode,
    convertKBtoMB,
    convertMbToKb,
    formattedAmount,
    convertLinkTextarea,
    convertedLinkInput,
    transferHTMLToShortcode,
    getCurrentDateTimeFormatted,
    convertToHalfsize,
    generateUniqueBlockName,
    isFullWidth,
    getTimeFormat,
    formatInputDayOrMonth,
    calculateDownloadProgress,
    convertHtmlLinks
};
